import { ref } from 'vue';
import { HttpError } from "@utils/models/HttpError";
import { SESSION_RENEWED_EVENT } from '@utils/session-events';

export const useFetch = (url: string) => {
  const baseURL = "/api";
  const loading = ref(false);

  const baseConfig: RequestInit = {
    headers: {
      Accept: "application/json",
    },
  };

  const mergeConfigs = (config?: RequestInit): RequestInit => ({
    ...baseConfig,
    ...config,
    headers: {
      ...(baseConfig.headers ?? {}),
      ...(config?.headers ?? {}),
    },
  });

  type Query = Record<string, string | number | boolean | undefined | null>;

  const get = async <T>(
    query?: Query,
    config?: RequestInit,
    options: HandleFetchOptions<T> = {},
  ): Promise<T> => {
    let queryString = '';
    const mergedConfig = { ...mergeConfigs(config), method: "GET" as const };

    if (query) {
      const parts = Object.entries(query)
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

      if (parts.length) {
        queryString = `?${parts.join('&')}`;
      }
    }

    return handleFetch(`${url}${queryString}`, mergedConfig, options);
  };

  const post = async <T>(data: unknown, config?: RequestInit): Promise<T> => {
    const isFormData = data instanceof FormData;

    const mergedConfig = mergeConfigs({
      ...config,
      method: "POST",
      body: isFormData ? data : JSON.stringify(data),
      headers: {
        ...(config?.headers ?? {}),
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
      },
    });

    return handleFetch(url, mergedConfig);
  };

  const put = async <T>(data?: unknown, config?: RequestInit): Promise<T> => {
    const isFormData = data instanceof FormData;

    const mergedConfig = mergeConfigs({
      ...config,
      method: "PUT",
      body: isFormData ? data : JSON.stringify(data),
      headers: {
        ...(config?.headers ?? {}),
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
      },
    });

    return handleFetch(url, mergedConfig);
  };

  const remove = async <T>(config?: RequestInit): Promise<T> => {
    const mergedConfig = { ...mergeConfigs(config), method: "DELETE" };
    return handleFetch(url, mergedConfig);
  };

  type HandleFetchOptions<T> = {
    allowedStatuses?: number[];
    mapAllowedBody?: (body: unknown, response: Response) => T;
    onError?: (err: HttpError) => void;
  };

  const handleFetch = async <T>(
    url: string,
    config: RequestInit,
    opts: HandleFetchOptions<T> = {},
  ): Promise<T> => {
    loading.value = true;
    try {
      const response = await fetch(`${baseURL}${url}`, config);
      const sessionExpiresIn = Number(response.headers.get("X-Signup-Session-Expires-In"));
      if (sessionExpiresIn > 0) {
        window.dispatchEvent(new CustomEvent(SESSION_RENEWED_EVENT, {
          detail: { sessionExpiresIn },
        }));
      }
      const contentType = response.headers.get("content-type") ?? "";
      const isJson = contentType.includes("application/json")
        || contentType.includes("+json");
      const body: unknown = isJson ? await response.json() : await response.text();

      if (opts.allowedStatuses?.includes(response.status)) {
        return opts.mapAllowedBody ? opts.mapAllowedBody(body, response) : (body as T);
      }

      if (!response.ok) {
        const message =
          typeof body === "string"
            ? body
            : typeof body === "object" && body !== null && "message" in body
              ? String((body as Record<string, unknown>).message)
              : response.statusText;

        const err = new HttpError(message, response.status, body);
        opts.onError?.(err);
        throw err;
      }

      return body as T;
    } finally {
      loading.value = false;
    }
  };

  return {
    get,
    post,
    put,
    remove,
    loading,
  };
};
